import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import PickerModal from '../components/PickerModal';
import { LANGUAGES } from '../data/languages';
import { extractDocxText } from '../utils/docx';
import '../mobile.css';

const NewChargeSheet = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setFullFIR } = useCaseContext();
  const fileInputRef = useRef(null);

  const fir = location.state?.fir;
  const id = fir?.id;

  const [language, setLanguage] = useState('English');
  const [chargeSheet, setChargeSheet] = useState('');
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleWordFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsParsingFile(true);
    setErrorMessage('');

    try {
      const text = await extractDocxText(file);
      setChargeSheet(text);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to read the Word document.');
    } finally {
      setIsParsingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const form = new FormData();
      form.append('id', id);
      form.append('language', language);
      form.append('chargeSheet', chargeSheet);

      const api = createApiClient(API_URL);
      const response = await api.post('/fir/chargesheet', form);
      setFullFIR(response.data);
      navigate('/m/fir/chargesheet/verified', { state: { fir: response.data } });
    } catch {
      setErrorMessage('Failed to upload charge sheet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!id) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No case selected.</p>;

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#094268', marginBottom: '15px' }}>Charge Sheet</h2>

      <div
        onClick={() => setShowLanguagePicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span>{language}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <textarea
        placeholder="Charge sheet text"
        value={chargeSheet}
        onChange={(e) => setChargeSheet(e.target.value)}
        rows={14}
        style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontFamily: 'inherit', fontSize: '14px' }}
      />

      <input ref={fileInputRef} type="file" accept=".docx" onChange={handleWordFileSelected} style={{ display: 'none' }} />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isParsingFile}
        className="m-pill-btn"
        style={{ color: '#094268', fontWeight: 600, marginBottom: '12px' }}
      >
        {isParsingFile ? 'Reading Word document...' : 'Upload Word Document'}
      </button>

      <button onClick={handleSubmit} disabled={loading || !chargeSheet.trim()} className="m-button-primary">
        {loading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Submit'}
      </button>

      {showLanguagePicker && (
        <PickerModal title="Language" options={LANGUAGES} selected={language} onSelect={setLanguage} onClose={() => setShowLanguagePicker(false)} />
      )}

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewChargeSheet;
